/**
 * resubscribeCopy.ts — the wire enums of the end-of-term re-subscribe lane,
 * turned into words. PURE: no React, no fetch, no dates.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: end-of-term re-subscribe.
 *
 * WHY ONE MODULE. Three surfaces (the admin drawer, the patient portal and the
 * public confirmation page) speak the same four enums, and each of them would
 * otherwise grow its own switch. A second copy is how "card declined" ends up
 * meaning two different things to two people looking at the same offer.
 *
 * WHY EXHAUSTIVE `Record<Enum, string>` MAPS RATHER THAN SWITCHES WITH A
 * DEFAULT. A default arm silently renders a raw wire enum ("no_payment_method")
 * to a patient the first time the server grows a value. A `Record` keyed by the
 * union makes that a COMPILE error instead, which is the only way this stays
 * true as the enums move.
 *
 * TWO AUDIENCES, DELIBERATELY DIFFERENT WORDS. The admin copy names the
 * mechanism because an admin has to act on it; the patient copy says what to do
 * next and never blames their card in language they cannot act on.
 *
 * R28: category-level only. Nothing here names a treatment — the medication is
 * always the server's `medicationName`, rendered verbatim from the API.
 */
import type {
  ResubscribeAuthorisationMethod,
  ResubscribeFailureReason,
  ResubscribeIneligibleReason,
  ResubscribeOfferState,
} from '../api/types';

/**
 * Why a finished plan cannot be re-subscribed — for the ADMIN, who is the only
 * person who ever sees a reason. A patient is never shown one: the entry point
 * simply is not there.
 *
 * `cancelled_mid_term` is the one worth reading twice. A plan cancelled before
 * term end is either an admin stopping it on request or a card that exhausted
 * the dunning ladder, and the subscription row cannot tell those apart. Offering
 * a re-subscribe to the second cohort just manufactures another failed charge,
 * so v1 blocks both and says so honestly rather than guessing.
 */
export const INELIGIBLE_REASON_COPY: Record<ResubscribeIneligibleReason, string> = {
  plan_still_live: 'This plan is still billing. A second concurrent supply is a double-prescribing risk.',
  never_started: 'This plan never started — nothing was approved or charged on it.',
  cancelled_mid_term:
    'This plan was cancelled before the end of its term, so we cannot tell whether the patient asked to stop or the payments failed. Take a new order instead.',
  already_resubscribed: 'This plan has already been re-subscribed.',
  offer_in_flight: 'A re-subscribe offer is already live for this plan. Wait for it, or withdraw it first.',
  no_patient_bound: 'No patient account is linked to this plan, so there is nobody to charge or email.',
  not_supplied: 'This plan was never approved and supplied, so there is nothing to repeat.',
};

/** The offer's state, for the ADMIN's eyes — what is happening and who we are waiting on. */
export const OFFER_STATE_COPY: Record<ResubscribeOfferState, string> = {
  pending_confirmation: 'Waiting for the patient to confirm',
  confirming: 'Confirming',
  payment_action_required: 'Payment needs attention',
  confirmed: 'Confirmed — with a clinician for review',
  expired: 'Expired',
  declined: 'Declined by the patient',
  withdrawn: 'Withdrawn',
};

/**
 * Why the off-session charge did not go through — ADMIN wording. All three
 * share one remedy (pay with any card via the emailed link), which is why they
 * share one offer state; the reason still has to be honest so the admin knows
 * whether to ring the patient about their bank.
 */
export const FAILURE_REASON_COPY_ADMIN: Record<ResubscribeFailureReason, string> = {
  card_declined: 'The saved card was declined.',
  no_payment_method: 'There is no saved card on this patient’s account.',
  requires_action: 'The bank asked the patient to authorise the payment themselves.',
};

/**
 * The same three, for the PATIENT. Deliberately blame-free and actionable — the
 * patient cannot do anything with "no_payment_method", but they can pay with a
 * card, and all three end in exactly that.
 */
export const FAILURE_REASON_COPY_PATIENT: Record<ResubscribeFailureReason, string> = {
  card_declined: 'We could not take payment with your saved card.',
  no_payment_method: 'We do not have a saved card for you.',
  requires_action: 'Your bank needs you to approve this payment yourself.',
};

/** How a LANE A admin took the patient's authorisation, in the order the radio group offers them. */
export const AUTHORISATION_METHODS: ReadonlyArray<{ value: ResubscribeAuthorisationMethod; label: string }> = [
  { value: 'phone_call', label: 'Phone call' },
  { value: 'video_call', label: 'Video call' },
  { value: 'in_person', label: 'In person' },
  { value: 'secure_message', label: 'Secure message' },
];

/**
 * The offer's time to live, in days. A COMMERCIAL number the CTO owns, not a
 * constant of nature: long enough to survive a holiday, short enough that the
 * price quoted in the email is still the price we charge. Every boundary is
 * expressed against this so changing it moves the whole lane at once.
 */
export const RESUBSCRIBE_OFFER_TTL_DAYS = 14;

/**
 * How stale a recorded LANE A authorisation may be. A GOVERNANCE number, and
 * the reason it exists at all: "he agreed last month" is not authorisation for a
 * charge today. Seven days is generous for a callback that took a day or two to
 * action, and the server re-checks it — this constant only stops the admin
 * wasting a round trip.
 */
export const MAX_AUTHORISATION_AGE_DAYS = 7;

/** `n month`/`n months` — used in three places, and getting it wrong once looks careless. */
export function monthsLabel(months: number): string {
  return `${months} month${months === 1 ? '' : 's'}`;
}
